/**
 * Whether extension settings can be persisted into the workbook.
 *
 * Two signals say a change will not survive a reload:
 *
 * - `viewing-mode`: the dashboard is open for viewing, not authoring. Known
 *   before any save is attempted.
 * - `permission-denied`: `saveAsync` rejected with PermissionDenied or 403.
 *   Known only after a save has already been tried and lost.
 *
 * The second is definite, so it outranks the first. Both are reported to the
 * user, because a save that silently does nothing looks identical to a save
 * that worked until the dashboard is reloaded.
 */

export type SavePermissionReason = 'viewing-mode' | 'permission-denied';

export interface SavePermissionInputs {
  /** `tableau.extensions.environment.mode`, or null if the host did not report one. */
  mode?: string | null;
  /** A saveAsync call has already been rejected as a permission failure. */
  saveDenied?: boolean;
}

export interface SavePermissionState {
  /** Changes are expected to survive a reload. */
  canPersist: boolean;
  reason: SavePermissionReason | null;
}

export interface SavePermissionMessage {
  title: string;
  detail: string;
}

export function isPermissionDeniedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('PermissionDenied') || message.includes('403');
}

export function resolveSavePermission(inputs: SavePermissionInputs): SavePermissionState {
  if (inputs.saveDenied) {
    return { canPersist: false, reason: 'permission-denied' };
  }
  if (inputs.mode === 'viewing') {
    return { canPersist: false, reason: 'viewing-mode' };
  }
  return { canPersist: true, reason: null };
}

export function describeSavePermission(reason: SavePermissionReason): SavePermissionMessage {
  if (reason === 'permission-denied') {
    return {
      title: 'Changes are not being saved',
      detail:
        'Tableau rejected the save: this role cannot store extension settings in the workbook. '
        + 'The tree and settings stay on screen for this session and are lost on reload. '
        + 'Ask someone with edit permission to load the tree and republish the dashboard.',
    };
  }
  return {
    title: 'Changes will not be saved',
    detail:
      'The dashboard is open in viewing mode. Trees and settings changed here apply to this '
      + 'session only and are discarded on reload. Edit the dashboard to store them in the workbook.',
  };
}
